import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Block, Sub, N, Conv, Bar, th, thr, td, tdr } from "./shared";
import { TIER_BANDS, FUNNEL_SHORT, fmtPct } from "../labels";

function Funnel({ f, drill }) {
  const max = Math.max(...f.steps.map((s) => s.count), 1);
  const stepHead = (steps) => steps.map((s) => <th key={s.step} className={thr} title={s.label}>{FUNNEL_SHORT[s.step]}</th>);
  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]" data-testid="kk-funnel-table">
          <thead><tr className="border-b border-clay-300"><th className={th}>Dónde se perdió</th><th className={thr}>Leads</th><th className={thr}>%</th><th className={thr}>Días (mediana)</th><th className={thr}>Último en hablar: nosotros</th></tr></thead>
          <tbody>
            {f.steps.map((s) => (
              <tr key={s.step} className="border-b border-clay-100" data-testid={`kk-funnel-${s.step}`}>
                <td className={td}>{s.label}</td>
                <td className={tdr}><Bar value={s.count} max={max}/> <N value={s.count} onClick={() => drill({ bucket: "not_sold", funnel_step: s.step })}/></td>
                <td className={tdr}>{fmtPct(s.pct)}</td><td className={tdr}>{s.median_days ?? "—"}</td><td className={tdr}>{fmtPct(s.last_agent_pct)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        {[["by_agent", "Por agente", (k) => ({ agent: k || "__none__" })], ["by_tier", "Por categoría", (k) => ({ tier: k })]].map(([key, title, extra]) => (
          <Sub key={key} title={title} hint="nº de perdidos en cada paso">
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]" data-testid={`kk-funnel-${key}`}>
                <thead><tr className="border-b border-clay-300"><th className={th}>{title === "Por agente" ? "Agente" : "Categoría"}</th><th className={thr}>Perdidos</th>{stepHead(f.steps)}</tr></thead>
                <tbody>
                  {f[key].map((r) => (
                    <tr key={r.key} className="border-b border-clay-100">
                      <td className={`${td} font-medium`}>{key === "by_tier" ? TIER_BANDS[r.key] || r.key : r.label}</td>
                      <td className={tdr}>{r.total}</td>
                      {f.steps.map((s) => <td key={s.step} className={tdr}><N value={r.steps[s.step]} onClick={() => drill({ bucket: "not_sold", funnel_step: s.step, ...extra(r.key) })}/></td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Sub>
        ))}
      </div>
    </>
  );
}

function Face({ face, drill }) {
  return (
    <>
      <Sub title="Medianas: vendidos frente a no vendidos" hint={`${face.sold} vendidos · ${face.lost} no vendidos`}>
        <div className="overflow-x-auto">
          <table className="text-[12px]" data-testid="kk-face-metrics">
            <thead><tr className="border-b border-clay-300"><th className={th}>Factor</th><th className={thr}>Vendidos</th><th className={thr}>No vendidos</th></tr></thead>
            <tbody>
              {face.metrics.map((m) => (
                <tr key={m.key} className="border-b border-clay-100"><td className={td}>{m.label}</td><td className={`${tdr} text-emerald-800`}>{m.sold ?? "—"}</td><td className={tdr}>{m.lost ?? "—"}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Sub>
      <Sub title="Conversión por tramo de cada factor" hint="ordenado por lo que más diferencia marca (tramos con ≥ 15 leads cerrados)">
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="kk-face-factors">
          {face.factors.map((f) => (
            <div key={f.key} className="border border-clay-200 p-3" data-testid={`kk-factor-${f.key}`}>
              <div className="flex items-baseline justify-between"><span className="text-[12px] font-medium">{f.label}</span><span className="text-[10px] text-clay-500 tabular">Δ {fmtPct(f.spread)}</span></div>
              <table className="w-full text-[11px] mt-1.5">
                <tbody>
                  {f.bands.map((b) => (
                    <tr key={String(b.band)} className="border-t border-clay-100">
                      <td className="py-1 pr-2">{b.label}</td>
                      <td className="py-1 text-right tabular"><N value={b.sold} onClick={() => drill({ bucket: "sold", [f.param]: b.band })} tone="text-emerald-800"/> / <N value={b.not_sold} onClick={() => drill({ bucket: "not_sold", [f.param]: b.band })}/></td>
                      <td className="py-1 pl-2 text-right"><Conv value={b.conversion}/></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      </Sub>
    </>
  );
}

function AiTagging({ ai, onDone }) {
  const [st, setSt] = useState(null);
  const load = () => api.get("/kimkim/ai-tags").then(({ data }) => setSt(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const running = st?.job?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(async () => {
      const { data } = await api.get("/kimkim/ai-tags");
      setSt(data);
      if (data.job?.status !== "running") onDone();
    }, 5000);
    return () => clearInterval(t);
  }, [running, onDone]);
  const start = async () => {
    try { await api.post("/kimkim/ai-tags"); toast.success("Etiquetado en marcha; puedes seguir trabajando"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo lanzar"); }
  };
  if (!st) return null;
  const j = st.job;
  return (
    <div className="border border-dashed border-clay-300 bg-clay-50 px-4 py-3 text-[12px] flex flex-wrap items-center gap-3" data-testid="kk-ai-tagging">
      <Sparkles size={14} className="text-terracotta"/>
      {running ? (
        <>
          <span>Etiquetando con IA… <strong className="tabular">{j.done}</strong> / {j.total}{j.failed ? ` · ${j.failed} fallos` : ""}</span>
          <span className="inline-block h-1.5 w-40 bg-clay-200"><span className="block h-1.5 bg-terracotta" style={{ width: `${j.total ? Math.round(100 * j.done / j.total) : 0}%` }}/></span>
        </>
      ) : st.pending > 0 ? (
        <>
          <span><strong>IA pendiente para {st.pending} leads</strong> cerrados · coste estimado ≈ {st.estimated_cost_eur.toFixed(2).replace(".", ",")} € (una sola vez; después la sincronización nocturna etiqueta solo los nuevos)</span>
          <button onClick={start} className="ml-auto px-3 py-1.5 bg-clay-900 text-white text-[12px] hover:bg-terracotta" data-testid="kk-ai-tag-start">Etiquetar con IA</button>
        </>
      ) : (
        <span>{st.tagged} leads etiquetados con IA · al día{j?.failed ? ` · ${j.failed} fallos en la última pasada` : ""}</span>
      )}
      {j?.status === "error" && <span className="text-terracotta">{j.error}</span>}
    </div>
  );
}

function Reasons({ ai, drill, labels }) {
  const max = Math.max(...ai.reasons.map((r) => r.count), 1);
  const rk = ai.reasons.map((r) => r.key);
  return (
    <>
      <div className="grid lg:grid-cols-2 gap-6">
        <Sub title="Motivo principal de pérdida (IA)" hint={`${ai.with_reason} de ${ai.lost} perdidos con motivo`}>
          <table className="w-full text-[12px]" data-testid="kk-ai-reasons">
            <tbody>
              {ai.reasons.map((r) => (
                <tr key={r.key} className="border-b border-clay-100" data-testid={`kk-ai-reason-${r.key}`}>
                  <td className={td}>{r.label}</td>
                  <td className={tdr}><Bar value={r.count} max={max}/> <N value={r.count} onClick={() => drill({ bucket: "not_sold", loss_reason: r.key })}/></td>
                  <td className={tdr}>{fmtPct(r.pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Sub>
        <Sub title="Motivo marcado en KimKim al archivar" hint="lo que puso la agente en KimKim">
          <table className="w-full text-[12px]" data-testid="kk-kimkim-reasons">
            <tbody>
              {ai.kimkim_reasons.map((r) => (
                <tr key={r.reason} className="border-b border-clay-100">
                  <td className={td}>{r.reason}</td>
                  <td className={tdr}><N value={r.count} onClick={() => drill({ bucket: "not_sold", kimkim_reason: r.reason })}/></td>
                  <td className={tdr}>{fmtPct(r.pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Sub>
      </div>
      {ai.with_reason > 0 && (
        <div className="grid lg:grid-cols-3 gap-6">
          {[["by_agent", "Por agente", (k) => ({ agent: k || "__none__" })], ["by_tier", "Por categoría", (k) => ({ tier: k })], ["by_destination", "Por destino", (k) => ({ destination: k })]].map(([key, title, extra]) => (
            <Sub key={key} title={title}>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]" data-testid={`kk-ai-${key}`}>
                  <thead><tr className="border-b border-clay-300"><th className={th}/>{rk.map((k) => <th key={k} className={thr} title={labels[k]}>{(labels[k] || k).split(" ")[0]}</th>)}</tr></thead>
                  <tbody>
                    {ai[key].map((r) => (
                      <tr key={r.key} className="border-b border-clay-100">
                        <td className={`${td} font-medium`}>{key === "by_tier" ? TIER_BANDS[r.key] || r.key : r.label} <span className="text-clay-500">({r.tagged})</span></td>
                        {rk.map((k) => <td key={k} className={tdr}><N value={r.reasons[k] || 0} onClick={() => drill({ bucket: "not_sold", loss_reason: k, ...extra(r.key) })}/></td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Sub>
          ))}
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-6">
        <Sub title="Quién habló último (perdidos)">
          <div className="text-[12px] space-y-1" data-testid="kk-last-speaker">
            <div>Nosotros: <strong className="tabular">{ai.last_speaker.agent}</strong> <span className="text-clay-500">({fmtPct(ai.lost ? Math.round(1000 * ai.last_speaker.agent / ai.lost) / 10 : null)})</span></div>
            <div>El viajero: <strong className="tabular">{ai.last_speaker.traveler}</strong></div>
          </div>
        </Sub>
        <Sub title="Nuestra primera respuesta (IA)" hint="personalizada frente a plantilla · conversión">
          <div className="text-[12px] space-y-1" data-testid="kk-first-reply">
            {["personalizada", "plantilla"].map((k) => { const v = ai.first_reply[k]; return <div key={k}>{k[0].toUpperCase() + k.slice(1)}: <N value={v.leads} onClick={() => drill({ first_reply: k })}/> leads → <Conv value={v.conversion}/></div>; })}
          </div>
        </Sub>
        {ai.objections.length > 0 && (
          <Sub title="Objeciones literales del viajero" hint="las más recientes">
            <ul className="text-[11px] space-y-1" data-testid="kk-objections">
              {ai.objections.map((o) => <li key={o.lead_id}><Link to={`/admin/kimkim-leads/${o.lead_id}`} className="underline decoration-dotted hover:text-terracotta">{o.traveler_name}</Link>: «{o.text}»</li>)}
            </ul>
          </Sub>
        )}
      </div>
    </>
  );
}

export function LossesBlock({ data, labels, drill, onTagged }) {
  const only = data.only;  // "face" | "losses" | undefined (everything)
  return (
    <Block id="losses" title={only === "face" ? "Vendidos frente a no vendidos, cara a cara" : "La pauta de los no vendidos"} subtitle="solo leads cerrados · los en curso no entran">
      {only !== "face" && <Sub title="a) Embudo de pérdida" hint={`${data.funnel.total} leads perdidos · en qué paso murió cada uno`}><Funnel f={data.funnel} drill={drill}/></Sub>}
      {only !== "losses" && <Sub title={only === "face" ? "Métricas y factores" : "b) Vendidos frente a no vendidos, cara a cara"}><Face face={data.face} drill={drill}/></Sub>}
      {only !== "face" && <Sub title="c) Etiquetado con IA">
        <div className="space-y-4">
          <AiTagging ai={data.ai} onDone={onTagged}/>
          <Reasons ai={data.ai} drill={drill} labels={labels.loss_reasons}/>
        </div>
      </Sub>}
    </Block>
  );
}
